/**
 * Web Audio API ringtone generator.
 * Produces a classic phone-ring pattern without any external audio files.
 */

let audioContext: AudioContext | null = null;
let oscillator: OscillatorNode | null = null;
let gainNode: GainNode | null = null;
let ringInterval: ReturnType<typeof setInterval> | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

/**
 * Start playing a ringing tone in a repeating pattern:
 * 400ms ring → 200ms silence → 400ms ring → 2000ms silence (repeat)
 */
export function startRingtone(): void {
  stopRingtone();

  const ctx = getAudioContext();
  if (ctx.state === "suspended") {
    ctx.resume();
  }

  gainNode = ctx.createGain();
  gainNode.gain.value = 0;
  gainNode.connect(ctx.destination);

  oscillator = ctx.createOscillator();
  oscillator.type = "sine";
  oscillator.frequency.value = 440;
  oscillator.connect(gainNode);
  oscillator.start();

  // Ring pattern: on 400ms, off 200ms, on 400ms, off 2000ms
  const pattern = [
    { gain: 0.3, duration: 400 },
    { gain: 0, duration: 200 },
    { gain: 0.3, duration: 400 },
    { gain: 0, duration: 2000 },
  ];
  const totalCycle = pattern.reduce((sum, p) => sum + p.duration, 0);

  let elapsed = 0;
  const applyPattern = () => {
    if (!gainNode) return;
    let t = elapsed % totalCycle;
    for (const step of pattern) {
      if (t < step.duration) {
        gainNode.gain.value = step.gain;
        return;
      }
      t -= step.duration;
    }
  };

  applyPattern();
  ringInterval = setInterval(() => {
    elapsed += 50;
    applyPattern();
  }, 50);
}

/**
 * Stop the ringing tone and clean up resources.
 */
export function stopRingtone(): void {
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
  if (oscillator) {
    try {
      oscillator.stop();
      oscillator.disconnect();
    } catch {
      /* already stopped */
    }
    oscillator = null;
  }
  if (gainNode) {
    gainNode.disconnect();
    gainNode = null;
  }
  // Close AudioContext to free resources (browsers limit concurrent contexts)
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
}
